import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-24">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-8 text-earth-800">Privacy Policy</h1>
          <div className="prose prose-earth max-w-none">
            <p className="text-lg text-earth-600 mb-6">
              Resonow ("we", "our", "us") is committed to protecting your privacy. This Privacy Notice explains how we collect, use, disclose, and safeguard your information when you use our platform and integrated services.
            </p>

            <div className="space-y-6 text-earth-600">
              <p>Last updated: February 19, 2026</p>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">1. Information We Collect</h2>
                <h3 className="text-xl font-semibold text-earth-700 mb-2">Information You Provide</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account Information:</strong> Name, email, password, phone (optional), birth date (optional), gender (optional), country/region.</li>
                  <li><strong>Profile Information:</strong> Profile picture, bio, qualifications, services, availability, address.</li>
                  <li><strong>Booking Information:</strong> Service details, session time, price, communication records.</li>
                  <li><strong>Payment Information:</strong> Processed securely via Stripe. We do not store full card details.</li>
                  <li><strong>Consent Records:</strong> When you accept our Terms of Service, Privacy Policy, SMS communications, or AI data usage consent, we log the consent event along with a timestamp, consent version, and browser information (user agent, IP address) for audit and compliance purposes.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">2. How We Use Your Information</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide and maintain our platform services.</li>
                  <li>Enable scheduling and management of client–practitioner sessions.</li>
                  <li>Process secure payments and bookings.</li>
                  <li>Communicate service updates and confirmations.</li>
                  <li>Verify practitioner qualifications.</li>
                  <li>Improve platform performance, security, and user experience.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">3. Information Sharing</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Between Clients and Practitioners:</strong> For service delivery and scheduling.</li>
                  <li><strong>With Service Providers:</strong> (e.g., Stripe for payments, Google APIs for meetings).</li>
                  <li><strong>Legal Obligations:</strong> When required by law, regulation, or valid legal request.</li>
                  <li><strong>Business Transfers:</strong> In case of a merger, acquisition, or similar event.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">4. Google Meet Integration</h2>
                <h3 className="text-xl font-semibold text-earth-700 mb-2">Data We Access and Why</h3>
                <p className="text-earth-600 mb-4">
                  When practitioners connect their Google accounts via OAuth, we obtain:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li>Access and refresh tokens</li>
                  <li>Token expiration time</li>
                  <li>Limited Google Calendar/Meet metadata (event ID, meeting link, start time)</li>
                </ul>
                <p className="text-earth-600">
                  This data is used exclusively to schedule and manage sessions on behalf of the practitioner within our platform.
                </p>
                
                <h3 className="text-xl font-semibold text-earth-700 mb-2 mt-6">How We Use and Protect This Data</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Tokens are securely stored using encryption-at-rest and transmitted only over HTTPS.</li>
                  <li>Tokens are used solely by our server-side processes to create and manage Google Meet sessions.</li>
                  <li>We do not share Google OAuth tokens or meeting data with third parties.</li>
                  <li>Practitioners may revoke access at any time by disconnecting Google from their account, which revokes our OAuth permissions.</li>
                </ul>

                <h3 className="text-xl font-semibold text-earth-700 mb-2 mt-6">Data Retention and Deletion</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>OAuth tokens are retained only while the integration is active and refreshed or revoked automatically.</li>
                  <li>Meeting metadata (e.g., join link, meeting ID, session time) may be stored for operational, billing, and audit purposes.</li>
                  <li>Upon disconnection, we immediately cease access to Google account data and delete or de-identify retained tokens in accordance with our retention policies and legal obligations.</li>
                  <li>Practitioners can request deletion of personal information at any time by contacting info@resonow.org.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">5. Data Protection and Security</h2>
                <p className="text-earth-600 mb-4">
                  We apply industry-standard technical and organizational safeguards to protect sensitive data:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Data Minimization:</strong> We collect and retain only the minimum necessary information to deliver services.</li>
                  <li><strong>Encryption:</strong> All personal and sensitive data is encrypted in transit (TLS 1.2/1.3) and at rest (AES-256).</li>
                  <li><strong>Access Controls:</strong> Role-based access (RBAC) ensures only authorized staff can access sensitive data. All administrative access requires multi-factor authentication (MFA).</li>
                  <li><strong>Monitoring & Auditing:</strong> We log and monitor access to sensitive systems. Regular audits and penetration tests are conducted to ensure security controls remain effective.</li>
                  <li><strong>Third-Party Security:</strong> Stripe handles all payment processing in compliance with PCI-DSS Level 1. Google Meet data is handled under Google's security and privacy framework.</li>
                  <li><strong>Compliance:</strong> We align our practices with the EU GDPR, UK GDPR, Singapore PDPA, US CCPA/CPRA, and other applicable data protection laws. Users can exercise rights to access, deletion, correction, and portability of their data. See Section 7 below for jurisdiction-specific rights.</li>
                  <li><strong>No Sale of Personal Data:</strong> We do not sell or rent personal data to third parties for marketing or advertising purposes.</li>
                  <li><strong>Consent Audit Trail:</strong> We maintain an immutable, append-only consent record log in compliance with GDPR Article 7, recording every consent grant and revocation event. We also maintain a data processing activity log per GDPR Article 30 to track access and processing of sensitive data throughout its lifecycle.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">6. Your Rights</h2>
                <p className="text-earth-600 mb-4">
                  Subject to applicable law, you have the right to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access, correct, or update your personal information.</li>
                  <li>Request deletion of your personal data.</li>
                  <li>Restrict or object to certain types of processing.</li>
                  <li>Request data portability.</li>
                  <li>Withdraw consent at any time (e.g., by disconnecting your Google account).</li>
                </ul>
                <p className="text-earth-600 mt-4">
                  You may exercise these rights by contacting info@resonow.org. You can also submit data rights requests (access, deletion, correction, and data export) directly through your account Settings under "Data & Privacy." We commit to responding to all requests within 30 days (or as required by applicable law). We may verify your identity before responding.
                </p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">7. International Data Protection Rights</h2>
                
                <h3 className="text-xl font-semibold text-earth-700 mb-2">🇪🇺 European Union (GDPR)</h3>
                <p className="text-earth-600 mb-2">If you are located in the European Economic Area (EEA), your personal data is processed under lawful bases including:</p>
                <ul className="list-disc pl-6 space-y-1 mb-4">
                  <li><strong>Consent:</strong> Where you have given explicit consent (e.g., marketing communications).</li>
                  <li><strong>Contract Performance:</strong> Where processing is necessary to fulfill our services to you.</li>
                  <li><strong>Legitimate Interest:</strong> Where processing is necessary for our legitimate business interests, balanced against your rights.</li>
                </ul>
                <p className="text-earth-600 mb-4">You have the right to: access, rectify, erase, restrict processing, data portability, and object to processing. You may also lodge a complaint with your local Data Protection Authority (DPA). Our Data Protection contact: <strong>info@resonow.org</strong>.</p>

                <h3 className="text-xl font-semibold text-earth-700 mb-2">🇬🇧 United Kingdom (UK GDPR)</h3>
                <p className="text-earth-600 mb-4">Post-Brexit, UK residents retain the same data protection rights under the UK GDPR. The Information Commissioner's Office (ICO) serves as the supervisory authority. You may contact the ICO at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline">ico.org.uk</a> for complaints or inquiries.</p>

                <h3 className="text-xl font-semibold text-earth-700 mb-2">🇸🇬 Singapore (PDPA)</h3>
                <p className="text-earth-600 mb-2">Under the Personal Data Protection Act (PDPA), we:</p>
                <ul className="list-disc pl-6 space-y-1 mb-4">
                  <li>Obtain your consent before collecting, using, or disclosing personal data.</li>
                  <li>Collect and use data only for purposes that a reasonable person would consider appropriate.</li>
                  <li>Provide access to and correction of your personal data upon request.</li>
                </ul>
                <p className="text-earth-600 mb-4">You may lodge complaints with the Personal Data Protection Commission (PDPC) at <a href="https://www.pdpc.gov.sg" target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline">pdpc.gov.sg</a>.</p>

                <h3 className="text-xl font-semibold text-earth-700 mb-2">🇺🇸 United States (CCPA/CPRA & State Laws)</h3>
                <p className="text-earth-600 mb-2">If you are a California resident (or resident of another US state with applicable privacy laws), you have the right to:</p>
                <ul className="list-disc pl-6 space-y-1 mb-4">
                  <li><strong>Right to Know:</strong> Request disclosure of the categories and specific pieces of personal information we have collected.</li>
                  <li><strong>Right to Delete:</strong> Request deletion of your personal information, subject to legal exceptions.</li>
                  <li><strong>Right to Opt-Out:</strong> Opt out of the "sale" or "sharing" of your personal information. <strong>We do not sell or share your personal data for advertising purposes.</strong></li>
                  <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights.</li>
                </ul>
                <p className="text-earth-600 mb-4">Categories of personal information we collect include: identifiers, account information, commercial information (bookings, payments), internet activity, and professional information (for practitioners).</p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">8. International Data Transfers</h2>
                <p className="text-earth-600 mb-4">
                  Your personal data may be transferred to and processed in countries outside your jurisdiction, including the United States. When we transfer personal data internationally, we implement appropriate safeguards to protect your data, including:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-4">
                  <li><strong>Standard Contractual Clauses (SCCs):</strong> We use EU-approved SCCs for transfers from the EEA/UK to countries without an adequacy decision.</li>
                  <li><strong>Adequacy Decisions:</strong> Where applicable, we rely on adequacy decisions by the European Commission or UK Secretary of State.</li>
                  <li><strong>Supplementary Measures:</strong> We apply additional technical and organizational measures (encryption, access controls) to ensure equivalent protection.</li>
                </ul>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">9. Data Retention</h2>
                <p className="text-earth-600 mb-4">
                  We retain your data for specific periods based on legal requirements and operational necessity. The following table summarizes our retention schedule:
                </p>
                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b-2 border-earth-200">
                        <th className="py-2 pr-4 font-semibold text-earth-800">Data Category</th>
                        <th className="py-2 pr-4 font-semibold text-earth-800">Retention Period</th>
                        <th className="py-2 font-semibold text-earth-800">Legal Basis</th>
                      </tr>
                    </thead>
                    <tbody className="text-earth-600">
                      <tr className="border-b border-earth-100">
                        <td className="py-2 pr-4">Session & clinical notes</td>
                        <td className="py-2 pr-4">7 years from creation</td>
                        <td className="py-2">HIPAA §164.530(j); state retention laws</td>
                      </tr>
                      <tr className="border-b border-earth-100">
                        <td className="py-2 pr-4">Session transcripts</td>
                        <td className="py-2 pr-4">7 years from creation</td>
                        <td className="py-2">HIPAA (clinical documentation)</td>
                      </tr>
                      <tr className="border-b border-earth-100">
                        <td className="py-2 pr-4">Booking & payment records</td>
                        <td className="py-2 pr-4">7 years from transaction date</td>
                        <td className="py-2">HIPAA; tax/financial compliance</td>
                      </tr>
                      <tr className="border-b border-earth-100">
                        <td className="py-2 pr-4">Messages (clinical context)</td>
                        <td className="py-2 pr-4">7 years from creation</td>
                        <td className="py-2">HIPAA (part of treatment record)</td>
                      </tr>
                      <tr className="border-b border-earth-100">
                        <td className="py-2 pr-4">Consent records</td>
                        <td className="py-2 pr-4">7 years from collection</td>
                        <td className="py-2">GDPR Article 7; HIPAA</td>
                      </tr>
                      <tr className="border-b border-earth-100">
                        <td className="py-2 pr-4">Account & profile data</td>
                        <td className="py-2 pr-4">Duration of account + 30 days after deletion</td>
                        <td className="py-2">Contract performance; GDPR Article 6(1)(b)</td>
                      </tr>
                      <tr className="border-b border-earth-100">
                        <td className="py-2 pr-4">Email delivery logs</td>
                        <td className="py-2 pr-4">180 days (6 months)</td>
                        <td className="py-2">Operational; no legal mandate</td>
                      </tr>
                      <tr className="border-b border-earth-100">
                        <td className="py-2 pr-4">Analytics & tracking events</td>
                        <td className="py-2 pr-4">90 days</td>
                        <td className="py-2">Operational; no legal mandate</td>
                      </tr>
                      <tr className="border-b border-earth-100">
                        <td className="py-2 pr-4">User session logs</td>
                        <td className="py-2 pr-4">30 days</td>
                        <td className="py-2">Operational; no legal mandate</td>
                      </tr>
                      <tr className="border-b border-earth-100">
                        <td className="py-2 pr-4">OAuth tokens (Google)</td>
                        <td className="py-2 pr-4">Until integration disconnected; then immediately deleted</td>
                        <td className="py-2">Consent; GDPR Article 6(1)(a)</td>
                      </tr>
                      <tr className="border-b border-earth-100">
                        <td className="py-2 pr-4">Data processing logs</td>
                        <td className="py-2 pr-4">7 years from creation</td>
                        <td className="py-2">GDPR Article 30 (records of processing activities)</td>
                      </tr>
                      <tr className="border-b border-earth-100">
                        <td className="py-2 pr-4">DSAR request records</td>
                        <td className="py-2 pr-4">7 years from resolution</td>
                        <td className="py-2">GDPR Articles 15-17 (compliance evidence)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-earth-600 mb-2">
                  Operational data (analytics events, email logs, session logs) is automatically purged on a daily schedule once the retention period expires. Clinical and legal records are retained for the full mandatory period regardless of account status.
                </p>
                <p className="text-earth-600">You may request deletion of your personal data at any time by contacting info@resonow.org. We will respond within 30 days (or as required by applicable law). Note that certain data may be retained beyond your request where required by law (e.g., clinical records under HIPAA).</p>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-earth-800 mb-4">10. Contact Us</h2>
                <p className="text-earth-600 mb-4">For privacy-related questions, please contact us at:</p>
                <p className="text-earth-600"><strong>Email:</strong> info@resonow.org</p>
                <h3 className="text-xl font-semibold text-earth-700 mb-2 mt-6">Data Protection Officer (DPO)</h3>
                <p className="text-earth-600 mb-2">
                  For GDPR, UK GDPR, and PDPA inquiries, you may contact our Data Protection Officer directly:
                </p>
                <p className="text-earth-600"><strong>DPO Email:</strong> dpo@resonow.org</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;